// "Good Enough" converters -- won't cover all edge cases, but more or less gets the job done

// #region CamelToSnake Converters
export type CamelToSnake<T extends string> = T extends `${infer U}${infer R}` ? `${U extends Capitalize<U> ? '_' : ''}${Lowercase<U>}${CamelToSnake<R>}` : T;

export type CamelKeysToSnake<T> = {
	[K in keyof T as CamelToSnake<Extract<K, string>>]: T[K];
};

export type CamelKeysToSnakeDeep<T> = T extends readonly any[]
	? { [K in keyof T]: CamelKeysToSnakeDeep<T[K]> }
	: T extends object
		? {
				[K in keyof T as CamelToSnake<Extract<K, string>>]: CamelKeysToSnakeDeep<T[K]>;
			}
		: T;
// #endregion

// #region SnakeToCamel Converters
export type SnakeToCamel<T extends string> = T extends `${infer U}_${infer R}` ? `${U}${Capitalize<SnakeToCamel<R>>}` : T;

export type SnakeKeysToCamel<T> = {
	[K in keyof T as SnakeToCamel<Extract<K, string>>]: T[K];
};

export type SnakeKeysToCamelDeep<T> = T extends readonly any[]
	? { [K in keyof T]: SnakeKeysToCamelDeep<T[K]> }
	: T extends object
		? {
				[K in keyof T as SnakeToCamel<Extract<K, string>>]: SnakeKeysToCamelDeep<T[K]>;
			}
		: T;
// #endregion

// #region PascalToCamel Converters
export type PascalToCamel<T extends string> = T extends `${infer U}${infer R}` ? `${Uncapitalize<U>}${R}` : T;

export type PascalKeysToCamel<T> = {
	[K in keyof T as PascalToCamel<Extract<K, string>>]: T[K];
};

export type PascalKeysToCamelDeep<T> = T extends readonly any[]
	? { [K in keyof T]: PascalKeysToCamelDeep<T[K]> }
	: T extends object
		? {
				[K in keyof T as PascalToCamel<Extract<K, string>>]: PascalKeysToCamelDeep<T[K]>;
			}
		: T;
// #endregion

// #region CamelToPascal Converters
export type CamelToPascal<T extends string> = T extends `${infer U}${infer R}` ? `${Capitalize<U>}${R}` : T;

export type CamelKeysToPascal<T> = {
	[K in keyof T as CamelToPascal<Extract<K, string>>]: T[K];
};

export type CamelKeysToPascalDeep<T> = T extends readonly any[]
	? { [K in keyof T]: CamelKeysToPascalDeep<T[K]> }
	: T extends object
		? {
				[K in keyof T as CamelToPascal<Extract<K, string>>]: CamelKeysToPascalDeep<T[K]>;
			}
		: T;
// #endregion

/** Wrapper around cloned RichTable data. */
export type RichTableRow<T> = T & {
	_ref: T;
	_index: number;
};

export function getRandomEnumValue<T extends { [key: string]: string | number }>(enumObj: T): T[keyof T] {
	// Filter out reverse mappings that TypeScript adds to number enums
	const enumValues = Object.keys(enumObj)
		.filter(key => isNaN(Number(key)))
		.map(key => enumObj[key]);

	// Ensure we have values to choose from
	if (enumValues.length === 0) {
		throw new Error('Enum has no values');
	}

	const randomIndex = Math.floor(Math.random() * enumValues.length);
	return enumValues[randomIndex] as T[keyof T];
}
